import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { ListingForm } from "@/components/admin/ListingForm";
import { TestimonialForm } from "@/components/admin/TestimonialForm";
import { DevelopmentForm } from "@/components/admin/DevelopmentForm";
import { SettingsForm } from "@/components/admin/SettingsForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Quote } from "lucide-react";
import { fmtPrice } from "@/utils/money";
import { LogOut, Plus, Pencil, Trash2, ArrowLeft, Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusBadge = (status, t) => ({
  sale: { label: t.listingsPage.forSale, cls: "bg-gold text-ink" },
  sold: { label: t.listingsPage.sold, cls: "bg-charcoal text-cream" },
  lease: { label: t.listingsPage.forLease, cls: "bg-emerald text-cream" },
}[status] || { label: status, cls: "bg-charcoal text-cream" });

export default function AdminDashboard() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState(null);
  const [listings, setListings] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [tFormOpen, setTFormOpen] = useState(false);
  const [tEditing, setTEditing] = useState(null);
  const [developments, setDevelopments] = useState([]);
  const [dFormOpen, setDFormOpen] = useState(false);
  const [dEditing, setDEditing] = useState(null);

  const token = localStorage.getItem("cv_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [leadsRes, listingsRes, testimonialsRes, developmentsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/listings`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/developments`),
      ]);
      setLeads(leadsRes.data);
      setListings(listingsRes.data);
      setTestimonials(testimonialsRes.data);
      setDevelopments(developmentsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("cv_admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [token, navigate, load]);

  const logout = () => {
    localStorage.removeItem("cv_admin_token");
    navigate("/admin/login");
  };

  const removeListing = async (id) => {
    if (!window.confirm(t.admin.deleteConfirm)) return;
    try {
      await axios.delete(`${API}/admin/listings/${id}`, { headers });
      toast.success(t.admin.deleted);
      load();
    } catch {
      toast.error(t.form.error);
    }
  };

  const LEAD_STATUSES = ["new", "contacted", "in_progress", "closed"];
  const leadStatusCls = {
    new: "bg-gold text-ink",
    contacted: "bg-emerald text-cream",
    in_progress: "bg-charcoal text-cream",
    closed: "bg-sand text-[#525D6B]",
  };

  const setLeadStatus = async (leadId, status) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { status }, { headers });
      toast.success(t.admin.statusUpdated);
    } catch {
      toast.error(t.form.error);
      load();
    }
  };

  const statusLabel = { sale: t.listingsPage.forSale, sold: t.listingsPage.sold, lease: t.listingsPage.forLease };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-cream pt-28 pb-20">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold-dark font-semibold">Carlos Vindel Realty</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink">{t.admin.dashboard}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-to-site" className="inline-flex items-center gap-2 text-sm text-[#525D6B] hover:text-gold-dark transition-colors">
              <ArrowLeft className="w-4 h-4" /> {t.admin.backToSite}
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-sand hover:border-ink px-4 py-2 text-sm font-semibold text-[#525D6B] hover:text-ink transition-colors"
            >
              <LogOut className="w-4 h-4" /> {t.admin.logout}
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[
            { key: "leads", label: t.admin.leadsTab },
            { key: "listings", label: t.admin.listingsTab },
            { key: "developments", label: t.admin.developmentsTab },
            { key: "testimonials", label: t.admin.testimonialsTab },
            { key: "settings", label: t.admin.settingsTab },
          ].map((tb) => (
            <button
              key={tb.key}
              data-testid={`admin-tab-${tb.key}`}
              onClick={() => setTab(tb.key)}
              className={`text-xs uppercase tracking-[0.2em] font-semibold px-5 py-2.5 rounded-full border transition-all duration-300 ${
                tab === tb.key ? "bg-ink text-cream border-ink" : "text-[#525D6B] border-sand hover:border-gold hover:text-gold-dark"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <div data-testid="admin-leads-panel" className="space-y-3">
            {!leads && <p className="text-[#8C96A4] text-sm">…</p>}
            {leads?.length === 0 && <p data-testid="admin-no-leads" className="font-serif italic text-xl text-[#8C96A4] py-10">{t.admin.noLeads}</p>}
            {leads?.map((lead) => (
              <article key={lead.id} data-testid={`lead-row-${lead.id}`} className="bg-white border border-sand/80 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <span className={`shrink-0 self-start text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full ${
                  lead.type === "valuation" ? "bg-emerald text-cream" : "bg-gold text-ink"
                }`}>
                  {lead.type === "valuation" ? t.admin.valuationType : t.admin.contactType}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink">{lead.name}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#525D6B]">
                    <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gold" />{lead.email}</span>
                    {lead.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gold" />{lead.phone}</span>}
                    {lead.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" />{lead.address}</span>}
                  </div>
                  {lead.message && <p className="mt-2 text-sm text-[#525D6B] italic">“{lead.message}”</p>}
                </div>
                <div className="shrink-0 flex sm:flex-col items-center sm:items-end gap-2">
                  <p className="text-xs text-[#8C96A4]">{new Date(lead.created_at).toLocaleString()}</p>
                  <Select value={lead.status || "new"} onValueChange={(v) => setLeadStatus(lead.id, v)}>
                    <SelectTrigger data-testid={`lead-status-${lead.id}`} className={`h-8 w-[140px] rounded-full border-0 text-[10px] uppercase tracking-[0.15em] font-semibold ${leadStatusCls[lead.status || "new"]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s, i) => (
                        <SelectItem key={s} value={s} data-testid={`lead-status-option-${s}`}>{t.admin.leadStatusOptions[i]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "listings" && (
          <div data-testid="admin-listings-panel">
            <button
              data-testid="admin-add-listing-btn"
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="mb-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm tracking-wide px-6 py-3 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t.admin.addListing}
            </button>
            <div className="space-y-3">
              {listings.map((l) => (
                <article key={l.id} data-testid={`listing-row-${l.id}`} className="bg-white border border-sand/80 p-4 flex items-center gap-4">
                  <img src={l.image} alt={l.address} className="w-20 h-16 object-cover shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{l.address}</p>
                    <p className="text-xs text-[#8C96A4] uppercase tracking-[0.15em] mt-0.5">{l.neighborhood}</p>
                    <p className="text-sm text-[#525D6B] mt-1">
                      ${Number(l.price).toLocaleString()}{l.status === "lease" ? t.listingsPage.perMonth : ""} · {l.beds}/{l.baths} · {Number(l.sqft).toLocaleString()} {t.listingsSection.sqft}
                      {l.featured && <span className="ml-2 text-gold-dark text-xs font-semibold">★ Featured</span>}
                    </p>
                  </div>
                  <span className={`hidden sm:inline-block text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full ${statusBadge(l.status, t).cls}`}>
                    {statusLabel[l.status]}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`edit-listing-${l.id}`}
                      onClick={() => { setEditing(l); setFormOpen(true); }}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-gold hover:text-gold-dark transition-colors"
                      aria-label={t.admin.editListing}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-listing-${l.id}`}
                      onClick={() => removeListing(l.id)}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-red-400 hover:text-red-600 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
        {tab === "testimonials" && (
          <div data-testid="admin-testimonials-panel">
            <button
              data-testid="admin-add-testimonial-btn"
              onClick={() => { setTEditing(null); setTFormOpen(true); }}
              className="mb-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm tracking-wide px-6 py-3 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t.admin.addTestimonial}
            </button>
            <div className="space-y-3">
              {testimonials.map((tst) => (
                <article key={tst.id} data-testid={`testimonial-row-${tst.id}`} className="bg-white border border-sand/80 p-5 flex items-start gap-4">
                  <Quote className="w-5 h-5 text-gold shrink-0 mt-1 rotate-180" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink">{tst.name}</p>
                    <p className="text-xs text-[#8C96A4] mt-0.5">{tst.context?.[lang] || tst.context?.en}</p>
                    <p className="mt-2 text-sm text-[#525D6B] italic line-clamp-2">“{tst.quote?.[lang] || tst.quote?.en}”</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`edit-testimonial-${tst.id}`}
                      onClick={() => { setTEditing(tst); setTFormOpen(true); }}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-gold hover:text-gold-dark transition-colors"
                      aria-label={t.admin.editTestimonial}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-testimonial-${tst.id}`}
                      onClick={async () => {
                        if (!window.confirm(t.admin.deleteTestimonialConfirm)) return;
                        try {
                          await axios.delete(`${API}/admin/testimonials/${tst.id}`, { headers });
                          toast.success(t.admin.deleted);
                          load();
                        } catch {
                          toast.error(t.form.error);
                        }
                      }}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-red-400 hover:text-red-600 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {tab === "developments" && (
          <div data-testid="admin-developments-panel">
            <button
              data-testid="admin-add-development-btn"
              onClick={() => { setDEditing(null); setDFormOpen(true); }}
              className="mb-6 inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink font-semibold text-sm tracking-wide px-6 py-3 transition-colors"
            >
              <Plus className="w-4 h-4" /> {t.admin.addDevelopment}
            </button>
            <div className="space-y-3">
              {developments.map((d) => (
                <article key={d.id} data-testid={`development-row-${d.id}`} className="bg-white border border-sand/80 p-4 flex items-center gap-4">
                  <img src={d.image} alt={d.name} className="w-20 h-16 object-cover shrink-0" loading="lazy" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-ink truncate">{d.name}</p>
                    <p className="text-xs text-[#8C96A4] uppercase tracking-[0.15em] mt-0.5">{d.area}</p>
                    <p className="text-sm text-[#525D6B] mt-1">
                      {fmtPrice(d.priceFrom)} – {fmtPrice(d.priceTo)}
                      {d.featured && <span className="ml-2 text-gold-dark text-xs font-semibold">★ Featured</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-testid={`edit-development-${d.id}`}
                      onClick={() => { setDEditing(d); setDFormOpen(true); }}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-gold hover:text-gold-dark transition-colors"
                      aria-label={t.admin.editDevelopment}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-development-${d.id}`}
                      onClick={async () => {
                        if (!window.confirm(t.admin.deleteDevelopmentConfirm)) return;
                        try {
                          await axios.delete(`${API}/admin/developments/${d.id}`, { headers });
                          toast.success(t.admin.deleted);
                          load();
                        } catch {
                          toast.error(t.form.error);
                        }
                      }}
                      className="w-9 h-9 border border-sand flex items-center justify-center text-[#525D6B] hover:border-red-400 hover:text-red-600 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {tab === "settings" && (
          <div data-testid="admin-settings-panel">
            <SettingsForm />
          </div>
        )}
      </div>

      <ListingForm open={formOpen} onOpenChange={setFormOpen} listing={editing} onSaved={load} />
      <DevelopmentForm open={dFormOpen} onOpenChange={setDFormOpen} development={dEditing} onSaved={load} />
      <TestimonialForm open={tFormOpen} onOpenChange={setTFormOpen} testimonial={tEditing} onSaved={load} />
    </main>
  );
}
